// 形状の構成データ（原作 `ShapeModel:setupShapePoints` / `ShapePosition` の移植 13個
// ＋ Web 版オリジナル2個 ＝ 15個）。
//
// ## どうやって起こしたか（2026-08-02）
//
// 原作は**画面座標**で組み立てていた。基準点から `offsetTriangleDown/Left/RightPoint:` を
// 繋いで大三角形の中心を1つずつ置いていく形で、座標が数式のまま散らばっている。
// これを**大三角形の整数グリッド `(R, C)`** へ読み替えた:
//
//     down → (R+1, C)   /   left → (R, C-1)   /   right → (R, C+1)
//
// 転記ミスを避けるため**原作を機械的に解析して書き出し**、次の3点が全形状で
// 成り立つことを確認してから貼っている:
//
//   1. 大三角形の数が仕様書の表と一致する（4/6/3/8/4/6/4/8/5/10/6/10/6）
//   2. 同じ形状に同じマスが二度出てこない
//   3. **導出したマスの向き `(R+C) の偶奇` が、原作の `DIRECTION_TOP/BOTTOM` 宣言と全一致**
//
// 3 が肝で、読み替えが間違っていれば必ずどこかで食い違う。全13形状・計80個の
// 大三角形すべてで一致した（＝ `down/left/right` の読み替えが正しいことの裏取り）。
//
// ※ 原作の `kShapeKindUnkown`（"?????"）は**遊べる形状ではない**（ロック中に出す
//   ハテナのアイコンで、小三角形スケールで組まれた別物）ので持たない。
//
// ⚠️ **形状名は翻訳しない**（固有名詞扱い・2026-08-01 決定）。カタログには入れない。
import { PANELS_PER_BIG_TRIANGLE } from '../config/gameConfig';
import { expandBigTriangle, cellKey, type Cell } from './geometry';

export interface ShapeDef {
  /** 形状名（英語のまま。翻訳対象外）。 */
  readonly name: string;
  /**
   * 構成する大三角形の位置 `[R, C]`。向きは `(R + C)` の偶奇から決まるので持たない
   * （原作は direction を明示していたが、**グリッドに載せた時点で冗長**になった）。
   */
  readonly big: readonly (readonly [number, number])[];
}

/**
 * 15形状。**先頭13個の並びは原作 `ShapeKindType` の順＝アンロックの進行順**
 * （添字 0..12 が形状番号 1..13）。仕様書の表と同じ順序・同じ大三角形数。
 * 末尾2個（Crown / Gate）は **Web 版オリジナル**（2026-08-03 追加。下の注記参照）。
 */
export const SHAPES: readonly ShapeDef[] = [
  { name: 'Triangle', big: [[0, 2], [1, 1], [1, 2], [1, 3]] },
  { name: 'Hexagon', big: [[0, 0], [0, 1], [0, 2], [1, 0], [1, 1], [1, 2]] },
  // 中央の ▲ は「穴」（原作も中心を addObject していない）。3つの ▼ が頂点だけで接する。
  { name: 'Three Triangle', big: [[0, 1], [0, 3], [1, 2]] },
  {
    name: 'Parallelogram',
    big: [[0, 2], [0, 3], [0, 4], [0, 5], [1, 1], [1, 2], [1, 3], [1, 4]],
  },
  { name: 'Twin Diamond', big: [[0, 0], [0, 2], [1, 0], [1, 2]] },
  { name: 'Drum', big: [[0, 1], [0, 2], [0, 3], [1, 1], [1, 2], [1, 3]] },
  { name: 'Twin Hourglass', big: [[0, 1], [0, 3], [1, 1], [1, 3]] },
  { name: 'Eagle', big: [[0, 0], [0, 1], [0, 2], [0, 3], [0, 4], [1, 1], [1, 2], [1, 3]] },
  { name: 'Five Triangle', big: [[0, 2], [0, 4], [1, 1], [1, 3], [1, 5]] },
  // 原作の表記は "U F O"（字間を空けた表示用）。仕様書の表記に合わせて詰める。
  {
    name: 'UFO',
    big: [[0, 0], [0, 1], [0, 2], [0, 3], [0, 4], [1, 0], [1, 1], [1, 2], [1, 3], [1, 4]],
  },
  { name: 'Three Diamond', big: [[0, 0], [0, 2], [0, 4], [1, 0], [1, 2], [1, 4]] },
  {
    name: 'Big Drum',
    big: [[0, 1], [0, 2], [0, 3], [0, 4], [0, 5], [1, 1], [1, 2], [1, 3], [1, 4], [1, 5]],
  },
  { name: 'Three Hourglass', big: [[0, 1], [0, 3], [0, 5], [1, 1], [1, 3], [1, 5]] },

  // ---- ここから Web 版オリジナル（2026-08-03 追加。原作 `ShapeKindType` には無い）----
  //
  // 追加の理由は**形状一覧が 5列×3行 = 15枠で、13 だと2つ空く**こと。
  //
  // ⚠️ **必ず末尾に足す**。記録の添字は `形状番号 × 3 + 難易度`（`progress.slotIndex`）なので、
  //    途中へ挿入すると**既存プレイヤーの記録が全部ずれる**。末尾なら `normalizeProgress` が
  //    39 → 45 枠へ空で埋めるだけで、既存の記録はそのまま残る。
  //
  // ⚠️ **5大列以内に収める**。盤面の一辺は幅で決まり（`fitLayout`）、5大列なら既存と同じ 84px、
  //    7大列にすると 63px まで縮む（＝最後の2形状だけ指で押しにくくなる）。
  //
  // 既存13種に無かった silhouette を選んである:
  //   Gate  … ベタの塊の中央に**三角形の穴**。穴の縁は隣接が欠けるので推理の質も変わる
  //           （Three Triangle の中央の穴は「3個が離れている」結果で、塊の中の穴ではない）
  //   Crown … ベタの台形に峰が3つ。**ギザギザの輪郭**を持つ唯一の形状
  { name: 'Gate', big: [[0, 0], [0, 1], [0, 2], [0, 3], [0, 4], [1, 0], [1, 1], [1, 3], [1, 4]] },
  { name: 'Crown', big: [[0, 0], [0, 2], [0, 4], [1, 0], [1, 1], [1, 2], [1, 3], [1, 4]] },
];

/** 形状の総数（13）。 */
export const SHAPE_COUNT = SHAPES.length;

/** 形状のパネル数（大三角形の数 × 16）。 */
export function panelCountOf(shapeIndex: number): number {
  return SHAPES[shapeIndex].big.length * PANELS_PER_BIG_TRIANGLE;
}

/**
 * 形状を構成する小三角形（パネル）を全部返す。
 * 大三角形ごとに16個へ展開するだけ＝**重複は出ない**（大三角形は敷き詰められるため。
 * geometry のテストで担保）。
 */
export function shapeCells(shapeIndex: number): Cell[] {
  const cells: Cell[] = [];
  for (const [R, C] of SHAPES[shapeIndex].big) cells.push(...expandBigTriangle(R, C));
  return cells;
}

/** 形状に属するマスの集合（盤面外の判定に使う）。 */
export function shapeCellSet(shapeIndex: number): Set<string> {
  return new Set(shapeCells(shapeIndex).map((p) => cellKey(p.r, p.c)));
}
